/**
 * Custom hook for managing card state and handlers
 * Extracted from Cards.js for better code organization
 * NOTE: Physical card functionality removed - Virtual cards are the exclusive offering
 */
import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { toast } from 'sonner';
import { 
  getVirtualCards, 
  createVirtualCard, 
  updateCardStatus, 
  deleteCard,
  getCardAnalytics,
  getCardTransactions,
  simulateCardSpending,
  getAllCardsAnalytics,
  getCardAlerts,
  getAllCardsAlerts,
  setCardBudgets,
  getCardBudgets,
  exportCardReport,
  getAIRecommendations,
  applyAIRecommendations,
  setCardBudgetSettings,
  getCardBudgetStatus,
  sendSpendingAlert,
  getNotificationPreferences,
  updateNotificationPreferences
} from '../lib/api';

export const useCardManagement = () => {
  // Core card state
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [cardType, setCardType] = useState('standard');
  const [showDetails, setShowDetails] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Analytics state
  const [cardAnalytics, setCardAnalytics] = useState(null);
  const [cardTransactions, setCardTransactions] = useState([]);
  const [overviewAnalytics, setOverviewAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Alerts & Budgets state
  const [cardAlerts, setCardAlerts] = useState(null);
  const [allAlerts, setAllAlerts] = useState([]);
  const [categoryBudgets, setCategoryBudgets] = useState({});
  const [alertThreshold, setAlertThreshold] = useState(80);
  const [budgetPeriods, setBudgetPeriods] = useState({});
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [savingBudgets, setSavingBudgets] = useState(false);
  
  // Simulation state
  const [simulatingSpending, setSimulatingSpending] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // AI Recommendations state
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [applyingRecommendations, setApplyingRecommendations] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  
  // Notification preferences state
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_alerts_enabled: true,
    alert_threshold_warning: 80,
    alert_threshold_critical: 95,
    budget_alerts_enabled: true,
    weekly_summary_enabled: false
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // ============ Data Fetching Functions ============
  
  const fetchCards = async () => {
    try {
      const response = await getVirtualCards();
      setCards(response.data);
      if (response.data.length > 0 && !selectedCard) {
        setSelectedCard(response.data[0]);
      }
    } catch (error) {
      logger.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverviewAnalytics = async () => {
    try {
      const response = await getAllCardsAnalytics();
      setOverviewAnalytics(response.data);
    } catch (error) {
      logger.error('Error fetching overview analytics:', error);
    }
  };

  const fetchAllAlerts = async () => {
    try {
      const response = await getAllCardsAlerts();
      setAllAlerts(response.data.alerts || []);
    } catch (error) {
      logger.error('Error fetching alerts:', error);
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      const response = await getNotificationPreferences();
      setNotificationPrefs(response.data);
    } catch (error) {
      logger.error('Error fetching notification preferences:', error);
    }
  };

  const fetchCardAnalytics = useCallback(async (cardId) => {
    setAnalyticsLoading(true);
    try {
      const [analyticsRes, transactionsRes, alertsRes, budgetsRes, budgetStatusRes] = await Promise.all([
        getCardAnalytics(cardId),
        getCardTransactions(cardId, 20),
        getCardAlerts(cardId),
        getCardBudgets(cardId),
        getCardBudgetStatus(cardId).catch(() => ({ data: null }))
      ]);
      setCardAnalytics(analyticsRes.data);
      setCardTransactions(transactionsRes.data);
      setCardAlerts(alertsRes.data);
      setCategoryBudgets(budgetsRes.data.category_budgets || {});
      setAlertThreshold(budgetsRes.data.overall_alert_threshold || 80);
      setBudgetStatus(budgetStatusRes.data);
    } catch (error) {
      logger.error('Error fetching card analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // ============ Event Handlers ============

  const handleCreateCard = async () => {
    setCreating(true);
    try {
      const response = await createVirtualCard(cardType);
      toast.success('Virtual card created successfully!');
      setDialogOpen(false);
      fetchCards();
      fetchOverviewAnalytics();
      setSelectedCard(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create card');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (cardId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'frozen' : 'active';
    try {
      await updateCardStatus(cardId, newStatus);
      toast.success(`Card ${newStatus === 'frozen' ? 'frozen' : 'unfrozen'} successfully`);
      fetchCards();
    } catch (error) {
      toast.error('Failed to update card status');
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await deleteCard(cardId);
      toast.success('Card deleted successfully');
      if (selectedCard?.id === cardId) {
        setSelectedCard(null);
        setCardAnalytics(null);
      }
      fetchCards();
      fetchOverviewAnalytics();
      fetchAllAlerts();
    } catch (error) {
      toast.error('Failed to delete card');
    }
  };

  const handleSimulateSpending = async (cardId) => {
    setSimulatingSpending(true);
    try {
      await simulateCardSpending(cardId, 10);
      toast.success('Simulated 10 transactions for demo');
      fetchCardAnalytics(cardId);
      fetchOverviewAnalytics();
      fetchAllAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to simulate spending');
    } finally {
      setSimulatingSpending(false);
    }
  };

  const handleSaveBudgets = async () => {
    if (!selectedCard) return;
    setSavingBudgets(true);
    try {
      const budgetSettings = {
        category_budgets: Object.fromEntries(
          Object.entries(categoryBudgets)
            .filter(([, amount]) => amount > 0)
            .map(([category, amount]) => [category, {
              amount: amount,
              period: budgetPeriods[category] || 'monthly'
            }])
        ),
        overall_alert_threshold: alertThreshold,
        budget_period: 'monthly'
      };
      
      await setCardBudgetSettings(selectedCard.id, budgetSettings);
      await setCardBudgets(selectedCard.id, {
        category_budgets: categoryBudgets,
        overall_alert_threshold: alertThreshold
      });
      
      toast.success('Budgets saved successfully');
      setBudgetDialogOpen(false);
      fetchCardAnalytics(selectedCard.id);
      fetchAllAlerts();
    } catch (error) {
      toast.error('Failed to save budgets');
    } finally {
      setSavingBudgets(false);
    }
  };

  const handleGetAIRecommendations = async () => {
    if (!selectedCard) return;
    setLoadingAI(true);
    try {
      const response = await getAIRecommendations(selectedCard.id);
      const rawRecommendations = response.data || [];
      const transformedData = {
        health_score: 75,
        summary: rawRecommendations.length > 0 
          ? `Based on your spending patterns, we've identified ${rawRecommendations.length} areas where you could optimize your budget.`
          : 'Great job! Your spending is well-optimized.',
        recommendations: rawRecommendations.map(rec => ({
          category: rec.category,
          message: rec.tips?.[0] || `Consider setting a budget of $${rec.recommended_budget.toFixed(0)} for ${rec.category}`,
          suggested_budget: rec.recommended_budget,
          potential_savings: rec.potential_savings,
          priority: rec.impact === 'high' ? 'high' : rec.impact === 'medium' ? 'medium' : 'low'
        })),
        warnings: rawRecommendations
          .filter(rec => rec.difficulty === 'hard')
          .map(rec => `${rec.category}: This may require significant lifestyle changes`)
      };
      setAiRecommendations(transformedData);
      setAiDialogOpen(true);
    } catch (error) {
      toast.error('Failed to get AI recommendations');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleApplyRecommendations = async () => {
    if (!selectedCard || !aiRecommendations?.recommendations) return;
    setApplyingRecommendations(true);
    try {
      await applyAIRecommendations(selectedCard.id, aiRecommendations.recommendations);
      toast.success('AI recommendations applied to your budgets!');
      setAiDialogOpen(false);
      fetchCardAnalytics(selectedCard.id);
      fetchAllAlerts();
    } catch (error) {
      toast.error('Failed to apply recommendations');
    } finally {
      setApplyingRecommendations(false);
    }
  };

  const handleSaveNotificationPrefs = async () => {
    setSavingNotifications(true);
    try {
      await updateNotificationPreferences(notificationPrefs);
      toast.success('Notification preferences saved');
      setNotificationDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSendTestAlert = async () => {
    if (!selectedCard) return;
    try {
      const response = await sendSpendingAlert(selectedCard.id);
      if (response.data.sent) {
        toast.success(`Test alert sent to ${response.data.notification.email}`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to send test alert');
    }
  };

  const handleExportCSV = async (cardId) => {
    setExporting(true);
    try {
      const card = cards.find(c => c.id === cardId);
      const lastFour = card?.card_number?.slice(-4) || 'card';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cards/${cardId}/export/csv`,
        { credentials: 'include' }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `card_${lastFour}_transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async (cardId) => {
    setExporting(true);
    try {
      const response = await exportCardReport(cardId);
      const report = response.data;
      
      const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <title>Card Report</title>
  <style>
    body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}
    h1{color:#022c22;border-bottom:2px solid #10b981;padding-bottom:10px}
    h2{color:#064e3b;margin-top:30px}
    .stat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin:20px 0}
    .stat{background:#f0fdf4;padding:15px;border-radius:8px}
    .stat-value{font-size:24px;font-weight:bold;color:#022c22}
    .stat-label{color:#64748b;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-top:20px}
    th,td{padding:10px;text-align:left;border-bottom:1px solid #e2e8f0}
    th{background:#f8fafc;font-weight:600}
    .category-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #e2e8f0}
    @media print{body{padding:20px}}
  </style>
</head>
<body>
  <h1>Card Statement Report</h1>
  <p>Card: **** ${String(report.card_info.last_four).replace(/[<>]/g, '')} (${String(report.card_info.card_type).replace(/[<>]/g, '')})</p>
  <p>Generated: ${new Date(report.report_generated_at).toLocaleString()}</p>
  <h2>Summary</h2>
  <div class="stat-grid">
    <div class="stat"><div class="stat-value">$${Number(report.summary.total_spent).toFixed(2)}</div><div class="stat-label">Total Spent</div></div>
    <div class="stat"><div class="stat-value">${Number(report.summary.transaction_count)}</div><div class="stat-label">Transactions</div></div>
    <div class="stat"><div class="stat-value">$${Number(report.summary.average_transaction).toFixed(2)}</div><div class="stat-label">Avg. Transaction</div></div>
  </div>
  <h2>Spending by Category</h2>
  ${Object.entries(report.category_breakdown).map(([cat, amount]) => 
    `<div class="category-row"><span>${String(cat).replace(/[<>]/g, '')}</span><span>$${Number(amount).toFixed(2)}</span></div>`
  ).join('')}
  <h2>Top Merchants</h2>
  ${report.top_merchants.map(m => 
    `<div class="category-row"><span>${String(m.merchant).replace(/[<>]/g, '')}</span><span>$${Number(m.amount).toFixed(2)}</span></div>`
  ).join('')}
  <h2>Recent Transactions</h2>
  <table>
    <thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th>Amount</th></tr></thead>
    <tbody>
      ${report.transactions.slice(0, 50).map(tx => 
        `<tr><td>${new Date(tx.date).toLocaleDateString()}</td><td>${String(tx.merchant).replace(/[<>]/g, '')}</td><td>${String(tx.category).replace(/[<>]/g, '')}</td><td>$${Number(tx.amount).toFixed(2)}</td></tr>`
      ).join('')}
    </tbody>
  </table>
</body>
</html>`;
      
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          URL.revokeObjectURL(url);
          printWindow.print();
        };
      }
      
      toast.success('Report generated');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  const toggleShowDetails = (cardId) => {
    setShowDetails(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  // Alert Helper Functions
  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'warning': return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      default: return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    }
  };

  // Effects
  useEffect(() => {
    fetchCards();
    fetchOverviewAnalytics();
    fetchAllAlerts();
    fetchNotificationPrefs();
  }, []);

  useEffect(() => {
    if (selectedCard) {
      fetchCardAnalytics(selectedCard.id);
    }
  }, [selectedCard, fetchCardAnalytics]);

  return {
    // State
    cards,
    loading,
    creating,
    cardType,
    setCardType,
    showDetails,
    dialogOpen,
    setDialogOpen,
    selectedCard,
    setSelectedCard,
    cardAnalytics,
    cardTransactions,
    overviewAnalytics,
    analyticsLoading,
    cardAlerts,
    allAlerts,
    categoryBudgets,
    setCategoryBudgets,
    alertThreshold,
    setAlertThreshold,
    budgetPeriods,
    setBudgetPeriods,
    budgetStatus,
    budgetDialogOpen,
    setBudgetDialogOpen,
    savingBudgets,
    simulatingSpending,
    exporting,
    aiRecommendations,
    loadingAI,
    applyingRecommendations,
    aiDialogOpen,
    setAiDialogOpen,
    notificationDialogOpen,
    setNotificationDialogOpen,
    notificationPrefs,
    setNotificationPrefs,
    savingNotifications,
    
    // Handlers
    handleCreateCard,
    handleToggleStatus,
    handleDeleteCard,
    handleSimulateSpending,
    handleSaveBudgets,
    handleGetAIRecommendations,
    handleApplyRecommendations,
    handleSaveNotificationPrefs,
    handleSendTestAlert,
    handleExportCSV,
    handleExportPDF,
    toggleShowDetails,
    
    // Helpers
    getAlertColor,
    fetchCardAnalytics,
    fetchAllAlerts,
    fetchOverviewAnalytics
  };
};
